/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { getEnvVarOptional, COSMOS_SITE_BASE_PATH } from "@cosmos/common-utils";

import { buildLocalizedUrl } from "./url.utils";

/**
 * A single entry in a schema.org `BreadcrumbList`.
 * @see https://developers.google.com/search/docs/appearance/structured-data/breadcrumb
 */
interface BreadcrumbListItem {
	"@type": "ListItem";
	position: number;
	name: string;
	item: string;
}

interface BreadcrumbList {
	"@context": "https://schema.org";
	"@type": "BreadcrumbList";
	itemListElement: BreadcrumbListItem[];
}

export interface BreadcrumbJsonLdOptions {
	/**
	 * Public URL path with a leading slash and the owner prefix already stripped
	 * (e.g. `/community/service-partners/acme`). This is the value templates build
	 * as `${rootSlug}${getSlugParam(params)}` — never the Hygraph slug.
	 */
	slug: string;
	/**
	 * Locale used to build item URLs. Pass `params.lang` (the public URL locale),
	 * matching how `og:url` is built — not the Hygraph locale code.
	 */
	locale: string;
	/**
	 * Human title for the current (leaf) page. Falls back to a humanized slug
	 * segment when omitted or blank.
	 */
	currentPageName?: string;
	/**
	 * Optional overrides for individual breadcrumb names, keyed by the cumulative
	 * public path of the node (e.g. `{ "/community": "Community Hub" }`). Takes
	 * precedence over `currentPageName` and humanization.
	 */
	ancestorNames?: Record<string, string>;
	/** Label for the Home root item. Defaults to `"Home"`. */
	homeLabel?: string;
	/** Whether to prepend a Home root item at position 1. Defaults to `true`. */
	includeHome?: boolean;
	/**
	 * Path prefixes for which no breadcrumb should be emitted, because their
	 * intermediate levels are not real navigable pages — Google flags trails whose
	 * items don't match the site's actual navigation. A prefix matches a slug that
	 * equals it or sits directly beneath it (`/plugins` excludes `/plugins/foo` but
	 * not `/plugins-store/foo`). Defaults to the comma-separated
	 * `BREADCRUMB_EXCLUDE_PREFIXES` env var, so each site can opt a section out
	 * without any code change.
	 */
	excludePrefixes?: string[];
}

/**
 * Minimum number of slug path segments for a page to be considered "nested" and
 * therefore worth a breadcrumb trail. `/about` (1 segment) is skipped;
 * `/plugins/foo` (2 segments) qualifies.
 */
const MIN_NESTED_SEGMENTS = 2;

/**
 * Turn a slug segment into a display name: split on `-`/`+`, capitalize each
 * word, join with spaces. `service-partners` → `Service Partners`. Returns the
 * raw segment if humanization yields nothing.
 */
const humanizeSlugSegment = (segment: string): string => {
	const humanized = segment
		.split(/[-+]/)
		.filter(Boolean)
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(" ");

	return humanized || segment;
};

/**
 * Normalize a denylist prefix to leading-slash, no-trailing-slash form so it can
 * be compared against a slug on segment boundaries.
 */
const normalizeExcludePrefix = (prefix: string): string => {
	const withLeadingSlash = prefix.startsWith("/") ? prefix : `/${prefix}`;
	return withLeadingSlash.length > 1 && withLeadingSlash.endsWith("/")
		? withLeadingSlash.slice(0, -1)
		: withLeadingSlash;
};

/**
 * Parse the per-site `BREADCRUMB_EXCLUDE_PREFIXES` env var (comma-separated path
 * prefixes) into a list. Unset/empty → no exclusions.
 */
const getExcludePrefixesFromEnv = (): string[] => {
	const raw = getEnvVarOptional("BREADCRUMB_EXCLUDE_PREFIXES");
	if (!raw) {
		return [];
	}

	return raw
		.split(",")
		.map((prefix) => prefix.trim())
		.filter(Boolean);
};

/**
 * True when `slug` falls under any denylist prefix — equals the prefix or sits
 * directly beneath it. Matches on segment boundaries so `/plugins` does not
 * exclude `/plugins-store`.
 */
const isExcludedByPrefix = (slug: string, prefixes: string[]): boolean =>
	prefixes.some((prefix) => {
		const normalized = normalizeExcludePrefix(prefix);
		return slug === normalized || slug.startsWith(`${normalized}/`);
	});

/**
 * Build an absolute URL for a breadcrumb `item`. Mirrors the base URL logic in
 * `buildLocalizedUrl`: in query-param locale mode `buildLocalizedUrl` already
 * returns an absolute, locale-clean URL; in path-based mode it returns a
 * relative `/{locale}{path}` which we make absolute against the same base.
 * Degrades to the relative path when `EPIC_SERVER_URL` is unset (e.g. build time).
 */
const toAbsoluteItemUrl = (locale: string, path: string): string => {
	const { localeSlug, plainUrl } = buildLocalizedUrl(locale, path);

	if (plainUrl.startsWith("http")) {
		return plainUrl;
	}

	const base = `${getEnvVarOptional("EPIC_SERVER_URL") ?? ""}${COSMOS_SITE_BASE_PATH()}`;
	return base ? `${base}${localeSlug}` : localeSlug;
};

/**
 * Serialize a JSON-LD value for safe inlining inside a `<script>` tag. Escaping
 * `<` to its `<` unicode form prevents a `</script>` sequence in any string
 * value from breaking out of the script element (the value is still valid JSON).
 * Kept internal so it does not collide on the metadata barrel export with the
 * `jsonLdSafeStringify` introduced by the structured-data work.
 */
const jsonLdSafeStringify = (value: unknown): string => JSON.stringify(value).replace(/</g, "\\u003c");

/**
 * Build `BreadcrumbList` schema.org JSON-LD for a page from its position in the
 * site hierarchy (the URL path), NOT from page body content. Returns a
 * script-safe JSON string, or `null` when the slug is not nested (fewer than two
 * path segments) so callers can skip rendering.
 *
 * Names are resolved hybrid-style, in precedence order: an explicit
 * `ancestorNames` override for the node's path, then `currentPageName` for the
 * leaf, then a humanized slug segment.
 */
export const buildBreadcrumbJsonLd = ({
	slug,
	locale,
	currentPageName,
	ancestorNames,
	homeLabel = "Home",
	includeHome = true,
	excludePrefixes,
}: BreadcrumbJsonLdOptions): string | null => {
	const segments = slug.split("/").filter(Boolean);

	if (segments.length < MIN_NESTED_SEGMENTS) {
		return null;
	}

	// Skip sections whose intermediate levels are not real navigable pages.
	const normalizedSlug = `/${segments.join("/")}`;
	if (isExcludedByPrefix(normalizedSlug, excludePrefixes ?? getExcludePrefixesFromEnv())) {
		return null;
	}

	const leafName = currentPageName?.trim();
	const itemListElement: BreadcrumbListItem[] = [];
	let position = 1;

	if (includeHome) {
		itemListElement.push({
			"@type": "ListItem",
			position,
			name: homeLabel,
			item: toAbsoluteItemUrl(locale, "/home"),
		});
		position += 1;
	}

	let cumulativePath = "";
	segments.forEach((segment, index) => {
		cumulativePath += `/${segment}`;
		const isLeaf = index === segments.length - 1;

		// Precedence: explicit override → leaf title → humanized segment.
		const name = ancestorNames?.[cumulativePath]?.trim() || (isLeaf ? leafName : "") || humanizeSlugSegment(segment);

		itemListElement.push({
			"@type": "ListItem",
			position,
			name,
			item: toAbsoluteItemUrl(locale, cumulativePath),
		});
		position += 1;
	});

	const breadcrumbList: BreadcrumbList = {
		"@context": "https://schema.org",
		"@type": "BreadcrumbList",
		itemListElement,
	};

	return jsonLdSafeStringify(breadcrumbList);
};
